import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ScoreBar from '../components/ScoreBar';
import RollingTips from '../components/RollingTips';
import ReactionBar from '../components/ReactionBar';
import CommentSection from '../components/CommentSection';
import AnimatedButton from '../components/AnimatedButton';

interface RatingData {
  id: number;
  roller_name: string;
  roll_consistency: number;
  shape_taper: number;
  filter_crutch: number;
  seal_quality: number;
  aesthetics: number;
  final_score: number;
  verdict: string;
  image_path: string;
  created_at: string;
  strain_name?: string;
  strain_type?: string;
  strain_effects?: string;
  tips?: string | string[];
  estimated_length_cm?: number;
  estimated_thickness_mm?: number;
  reactions: Record<string, number>;
  userReactions?: string[];
}

export default function RatingDetail() {
  const { id } = useParams<{ id: string }>();
  const [rating, setRating] = useState<RatingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showScores, setShowScores] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    const currentUser = localStorage.getItem('rollerName') || '';
    fetch(`/api/rating/${id}${currentUser ? `?username=${encodeURIComponent(currentUser)}` : ''}`)
      .then(res => {
        if (!res.ok) throw new Error('Not found');
        return res.json();
      })
      .then(data => {
        setRating(data);
        setLoading(false);
        setTimeout(() => setShowScores(true), 100);
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
  }, [id]);

  const handleShare = async () => {
    const url = window.location.href;
    const title = `Hashups: ${rating?.final_score}/10 by ${rating?.roller_name}`;
    const text = rating?.verdict || '';

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch {
        // User cancelled or share failed, fall through to clipboard
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard failed silently
    }
  };

  // Parse tips from JSON string or array
  const parseTips = (): string[] => {
    if (!rating?.tips) return [];
    // If already an array, return it
    if (Array.isArray(rating.tips)) return rating.tips;
    // If string, try to parse as JSON
    try {
      const parsed = JSON.parse(rating.tips);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  if (loading) {
    return (
      <div className="max-w-lg mx-auto px-3 py-12 text-center">
        <p className="text-cream/40 text-sm">Loading rating...</p>
      </div>
    );
  }

  if (notFound || !rating) {
    return (
      <div className="max-w-lg mx-auto px-3 py-12 text-center">
        <h2 className="text-xl font-bold text-cream mb-2">Rating Not Found</h2>
        <p className="text-cream/40 mb-4 text-sm">This rating doesn't exist or has been removed.</p>
        <Link to="/rate" className="text-cannabis-400 hover:text-cannabis-300 underline text-sm">
          Rate a joint instead
        </Link>
      </div>
    );
  }

  const tips = parseTips();
  const scoreColor =
    rating.final_score >= 7 ? 'text-cannabis-300' :
    rating.final_score >= 4 ? 'text-warm' :
    'text-red-400';

  return (
    <div className="max-w-lg mx-auto px-3 py-4 space-y-3">
      {/* Joint Image */}
      {rating.image_path && (
        <div className="bg-cannabis-800 rounded-2xl overflow-hidden border border-cannabis-700/30">
          <img
            src={rating.image_path}
            alt={`Joint by ${rating.roller_name}`}
            className="w-full max-h-80 object-contain"
          />
        </div>
      )}

      {/* Score + Dimensions */}
      <div className="bg-cannabis-800 rounded-2xl p-4 border border-cannabis-700/30">
        <div className="text-center mb-4">
          <div className={`text-4xl font-bold ${scoreColor}`}>
            {rating.final_score.toFixed(2)} <span className="text-xl text-cream/30">/ 10</span>
          </div>
          <div className="text-cream/35 text-xs mt-0.5">Final Score</div>
        </div>

        <div className="space-y-2">
          <ScoreBar label="Roll Consistency" score={showScores ? rating.roll_consistency : 0} />
          <ScoreBar label="Shape & Taper" score={showScores ? rating.shape_taper : 0} />
          <ScoreBar label="Filter/Crutch" score={showScores ? rating.filter_crutch : 0} />
          <ScoreBar label="Seal Quality" score={showScores ? rating.seal_quality : 0} />
          <ScoreBar label="Aesthetics" score={showScores ? rating.aesthetics : 0} />
        </div>
      </div>

      {/* Verdict */}
      <div className="bg-cannabis-800/50 rounded-2xl p-3.5 border border-cannabis-700/30">
        <p className="text-cream/80 italic text-sm leading-relaxed">"{rating.verdict}"</p>
      </div>

      {/* Rolling Tips */}
      {tips.length > 0 && <RollingTips tips={tips} />}

      {/* Roller + Metadata */}
      <div className="bg-cannabis-800/50 rounded-2xl p-3.5 border border-cannabis-700/30 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-cream/40 text-xs">Rolled by</span>
          <Link
            to={`/profile/${encodeURIComponent(rating.roller_name)}`}
            className="text-cannabis-400 hover:text-cannabis-300 font-semibold text-sm transition"
          >
            {rating.roller_name}
          </Link>
        </div>

        {rating.strain_name && (
          <div className="flex items-center justify-between">
            <span className="text-cream/40 text-xs">Strain</span>
            <span className="flex items-center gap-1.5">
              <span className="text-cream text-sm">{rating.strain_name}</span>
              {rating.strain_type && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                  rating.strain_type === 'Sativa' ? 'bg-yellow-500/20 text-yellow-300' :
                  rating.strain_type === 'Indica' ? 'bg-purple-500/20 text-purple-300' :
                  'bg-cannabis-500/20 text-cannabis-300'
                }`}>{rating.strain_type}</span>
              )}
            </span>
          </div>
        )}

        {rating.strain_effects && (
          <div className="flex items-center justify-between">
            <span className="text-cream/40 text-xs">Effects</span>
            <span className="text-cream/60 text-xs text-right max-w-[60%]">{rating.strain_effects}</span>
          </div>
        )}

        {(rating.estimated_length_cm || rating.estimated_thickness_mm) && (
          <div className="flex items-center justify-between">
            <span className="text-cream/40 text-xs">Estimated Size</span>
            <span className="text-cream/60 text-xs">
              {rating.estimated_length_cm ? `${rating.estimated_length_cm} cm` : ''}
              {rating.estimated_length_cm && rating.estimated_thickness_mm ? ' x ' : ''}
              {rating.estimated_thickness_mm ? `${rating.estimated_thickness_mm} mm` : ''}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className="text-cream/40 text-xs">Rated</span>
          <span className="text-cream/60 text-xs">
            {new Date(rating.created_at).toLocaleDateString('en-US', {
              year: 'numeric', month: 'short', day: 'numeric',
            })}
          </span>
        </div>
      </div>

      {/* Reactions & Comments */}
      <ReactionBar
        ratingId={rating.id}
        username={localStorage.getItem('rollerName') || ''}
        initialReactions={rating.reactions}
        initialUserReactions={rating.userReactions || []}
      />
      <CommentSection
        ratingId={rating.id}
        username={localStorage.getItem('rollerName') || ''}
        isAdmin={(() => {
          try {
            const s = localStorage.getItem('jointRaterSession');
            return s ? JSON.parse(s)?.isAdmin === true : false;
          } catch { return false; }
        })()}
      />

      {/* Share Button */}
      <AnimatedButton onClick={handleShare} className="w-full">
        {copied ? 'Link Copied!' : 'Share This Rating'}
      </AnimatedButton>
    </div>
  );
}
